import { trace } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
} from '@opentelemetry/semantic-conventions/incubating'
import { DONEVERSE_ENV, DONEVERSE_RELEASE, OBSERVABILITY_ENABLED } from './env'

let initialized = false

function instrumentSpaNavigations() {
  const tracer = trace.getTracer('doneverse-dashboard')

  const capture = () => {
    const url = window.location.href
    const span = tracer.startSpan('navigation')
    span.setAttribute('url.full', url)
    span.setAttribute('url.path', window.location.pathname)
    span.setAttribute('url.query', window.location.search)
    span.setAttribute('navigation.type', 'spa')
    requestAnimationFrame(() => span.end())
  }

  const originalPushState = history.pushState.bind(history)
  history.pushState = (...args) => {
    originalPushState(...args)
    capture()
  }

  const originalReplaceState = history.replaceState.bind(history)
  history.replaceState = (...args) => {
    originalReplaceState(...args)
    capture()
  }

  window.addEventListener('popstate', capture)
}

export function initObservability() {
  if (initialized || !OBSERVABILITY_ENABLED) return

  /*
   * Use API proxy for traces to avoid CORS issues and keep tokens secure
   * The API handles forwarding to Better Stack
   */
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'

  const exporter = new OTLPTraceExporter({
    url: `${apiUrl}/v1/telemetry/traces`,
    // No auth header - API handles authentication to Better Stack
  })

  const resource = defaultResource().merge(
    resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'doneverse-dashboard',
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: DONEVERSE_ENV,
      // Omitted, not placeholder-filled, when the build was not stamped.
      ...(DONEVERSE_RELEASE ? { [ATTR_SERVICE_VERSION]: DONEVERSE_RELEASE } : {}),
    }),
  )

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(exporter)],
  })
  provider.register()

  const propagateTraceHeaderCorsUrls: RegExp[] = [/api\.doneverse\.com/, /localhost:3001/]

  if (import.meta.env.VITE_API_URL) {
    try {
      const hostname = new URL(import.meta.env.VITE_API_URL).hostname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      propagateTraceHeaderCorsUrls.push(new RegExp(hostname))
    } catch {
      // ignore invalid API URL
    }
  }

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls,
      }),
      new DocumentLoadInstrumentation(),
    ],
  })

  instrumentSpaNavigations()

  initialized = true
  if (import.meta.env.DEV) {
    console.info('OpenTelemetry initialized (browser)')
  }
}
